if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      this.filteringOption = this.dataset.filteringOption;
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

      customElements.whenDefined('slider-component').then(() => {
        requestAnimationFrame(() => {
          this.syncViewerSliderReferenceSlide();
          this.syncInitialActiveThumbnail();
          if (typeof ResizeObserver === 'undefined' || !this.elements.viewer?.slider) return;
          this._viewerSliderRefObserver?.disconnect();
          this._viewerSliderRefObserver = new ResizeObserver(() => this.syncViewerSliderReferenceSlide());
          this._viewerSliderRefObserver.observe(this.elements.viewer.slider);
        });
      });
    }

    disconnectedCallback() {
      this._viewerSliderRefObserver?.disconnect();
    }

    /**
     * Si la 1re slide est masquée (ex. display:none), le slider utilise une largeur 0 pour le scroll des points.
     * On pointe alors sur la première slide réellement visible.
     */
    syncViewerSliderReferenceSlide() {
      const viewer = this.elements.viewer;
      if (!viewer?.slider || !viewer.sliderFirstItemNode) return;
      const slides = viewer.slider.querySelectorAll('.slider__slide');
      if (!slides.length) return;
      const first = slides[0];
      if (first && first.getBoundingClientRect().width > 0) return;
      const firstVisible = Array.from(slides).find((el) => el.getBoundingClientRect().width > 0);
      if (firstVisible) viewer.sliderFirstItemNode = firstVisible;
    }


    /**
     * Slide « principale » au centre du carrousel (grid--peek : le thème peut sinon
     * signaler la slide de gauche / currentPage décalé d’une unité).
     */
    getDominantSlideMediaId() {
      const slider = this.elements.viewer?.slider;
      if (!slider) return null;

      const slides = [...slider.querySelectorAll('.slider__slide')].filter(
        (el) => el.clientWidth > 0 && !el.classList.contains('hidden')
      );
      if (!slides.length) return null;

      const view = slider.getBoundingClientRect();
      const anchorX = view.left + view.width / 2;

      let best = slides[0];
      let bestDist = Infinity;
      for (const slide of slides) {
        const r = slide.getBoundingClientRect();
        if (r.width <= 0) continue;
        const cx = r.left + r.width / 2;
        const dist = Math.abs(cx - anchorX);
        if (dist < bestDist) {
          bestDist = dist;
          best = slide;
        }
      }
      return best.dataset.mediaId || null;
    }

    syncPaginationDots(mediaId) {
      const viewer = this.elements.viewer;
      const slider = viewer?.slider;
      if (!slider || !mediaId) return;

      const visibleSlides = [...slider.querySelectorAll('.slider__slide')].filter(
        (el) => el.clientWidth > 0 && !el.classList.contains('hidden')
      );
      const slideIndex = visibleSlides.findIndex((el) => el.dataset.mediaId === mediaId);
      if (slideIndex < 0) return;

      const dots = [...viewer.querySelectorAll('.slider__dots .slider-counter__link--dots')].filter(
        (btn) => !btn.classList.contains('hidden')
      );
      if (!dots.length || slideIndex >= dots.length) return;

      dots.forEach((btn, i) => {
        const active = i === slideIndex;
        btn.classList.toggle('slider-counter__link--active', active);
        if (active) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });
    }

    onSlideChanged(event) {
      requestAnimationFrame(() => {
        const mediaId =
          this.getDominantSlideMediaId() ||
          event.detail.currentElement?.dataset?.mediaId;
        if (!mediaId) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (thumbnail) this.setActiveThumbnail(thumbnail);
        this.syncPaginationDots(mediaId);
      });
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach(slide => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        })
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'auto' });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        }
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    markThumbnailCurrent(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;
      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      const button = thumbnail.querySelector('button');
      if (button) button.setAttribute('aria-current', 'true');
    }

    syncInitialActiveThumbnail() {
      if (!this.elements.thumbnails || !this.elements.viewer) return;
      const activeSlide =
        this.elements.viewer.querySelector('.product__media-item.is-active:not(.hidden)') ||
        [...this.elements.viewer.querySelectorAll('.product__media-item')].find(
          (el) => !el.classList.contains('hidden') && el.getClientRects().length
        );
      if (!activeSlide?.dataset.mediaId) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeSlide.dataset.mediaId}"]`);
      this.markThumbnailCurrent(thumbnail);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.markThumbnailCurrent(thumbnail);

      const verticalThumbs =
        this.elements.thumbnails.classList.contains('thumbnail-slider--vertical-desktop') &&
        window.matchMedia('(min-width: 990px)').matches;
      if (!verticalThumbs && this.elements.thumbnails.isSlideVisible(thumbnail, false, 10)) return;

      const sliderEl = this.elements.thumbnails.slider;
      if (verticalThumbs) {
        sliderEl.scrollTo({ top: thumbnail.offsetTop, behavior: 'auto' });
      } else {
        sliderEl.scrollTo({ left: thumbnail.offsetLeft, behavior: 'auto' });
      }
    }


    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
